/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Button, HStack, Td, Tr, Tooltip, Box, VStack, Image, Checkbox, useToast } from '@chakra-ui/react';
import { DashboardHooks } from '@partiu/api/dashboard/hooks';
import CreateMapintegration, { CreateMapintegrationRef, mapIntegrationPlanProduct } from '../../CreateMapintegration';
import BodyTable from './BodyTable.tsx';
import { badges } from '../../../ModulesSuperApp/steps/Step4.tsx';
import { ProductERP } from '@partiu/api/dashboard/types.ts';
import { Plan, Product } from '../interfaces';

interface IntegrationPlanRowProps {
  productERP: ProductERP;
  refetch: () => void;
  products: Product[];
  extraData?: React.ReactNode;
  allContrated?: boolean;
  hasSvas?: boolean;
  onlyContrated?: boolean;
  hasListFilter?: boolean;
  visiblePlans?: ProductERP[];
}

export const IntegrationPlanRow = React.memo<IntegrationPlanRowProps>(({
  productERP,
  refetch,
  onlyContrated,
  products,
  allContrated,
  extraData,
  hasSvas,
  hasListFilter = false,
  visiblePlans,
}) => {
  const queryClient = useQueryClient();
  const createMapintegrationRef = useRef<CreateMapintegrationRef>(null);
  const toast = useToast();
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);

  const productsWithDefault = React.useMemo(() => {
    if (hasSvas) {
      return products.filter((product) => product.default_product.id === 4);
    }
    const result: any[] = products.map((defaultProduct) => {
      const found = products.find((product) => product.default_product.id === defaultProduct.id);
      if (found) {
        return { ...found, active: true };
      }
      if (onlyContrated) return null;
      return defaultProduct;
    });
    return result.filter(Boolean);
  }, [products, onlyContrated, hasSvas]);

  const handleDirectUpdate = async (targets: ProductERP[], product: Product, planSelected: Plan | null) => {
    if (!targets.length) return;
    const actionKey = `${product.id}-${planSelected?.id || 'null'}`;
    setPendingActionKey(actionKey);

    try {
      const results = await Promise.all(
        targets.map((plan) =>
          mapIntegrationPlanProduct({
            product,
            productERP: plan,
            planFound: planSelected || null,
          })
        )
      );

      const totalProcessed = targets.length;
      const firstPlan = targets[0];
      const firstResult = results[0];

      const namePlan = firstPlan?.name_plan || firstPlan?.plan_slug_integration;
      toast({
        title: totalProcessed > 1 ? 'Planos atualizados' : firstResult === 'deleted' ? 'Produto removido' : 'Produto vinculado',
        description:
          totalProcessed > 1
            ? `${totalProcessed} plano(s) foram atualizados conforme o estado atual da listagem.`
            : firstResult === 'deleted'
              ? `${product?.name} foi removido do plano ${namePlan}`
              : `${planSelected?.name || product?.name} foi vinculado ao plano ${namePlan}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetch();
      queryClient.refetchQueries([DashboardHooks.Modules]);
      queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      queryClient.refetchQueries([DashboardHooks.ErpPlans]);

      setPendingActionKey(null);
    } catch (error) {
      toast({
        title: 'Erro ao atualizar plano',
        description: 'Tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPendingActionKey(null);

    }
  };

  const hasFilterActive = Boolean(hasListFilter && visiblePlans && visiblePlans.length > 0);

  const handleBadgeClick = (p: Product, planFound: Plan | null) => {
    if (hasFilterActive && visiblePlans) {
      createMapintegrationRef.current?.open(p as any, productERP, planFound, {
        targets: visiblePlans,
        fromFilter: true,
      });
      return;
    }

    handleDirectUpdate([productERP], p, planFound);
  };

  console.log(productERP)
  return (
    <Tr _hover={{ bg: 'gray.50' }} transition="background 0.2s">
      <BodyTable
        id_plan={productERP?.id || ''}
        name_plan={productERP?.plan_slug_integration || ''}
        value_plan={productERP?.value_plan || productERP?.price_to}
        count_clients_active={productERP?.count_clients_active || 0}
      />

      {productsWithDefault.map((p) => {
        if (!p) {
          return null;
        }

        return (
          <Td key={p.id} minW={p.plans.length !== 0 ? "200px" : "80px"} maxW={p.plans.length !== 0 ? "200px" : "80px"} textAlign="center" py={2} borderRight="1px solid" borderColor="gray.200">
            <HStack spacing={1} flexWrap="wrap" w="100%" alignItems="center" justifyContent="center">
              {p.plans
                .sort((a, b) => a.level.rank - b.level.rank)
                .map((planFound) => {
                  const badge = badges.find((badge) => badge.level === planFound.level.rank);

                  const isContracted = hasSvas ? p.active !== false : (allContrated ? true : p.active);

                  const currentProductFound = productERP.products.find((product) => product.product === p.id);
                  const isCurrentPlan = planFound.id === currentProductFound?.plan;

                  if (badge) {
                    return (
                      <Tooltip
                        key={planFound.id}
                        label={
                          !isContracted
                            ? `Módulo desabilitado - Clique para mapear ${planFound.name} (Nível ${planFound.level?.name}) no plano ${productERP.name_plan || productERP.plan_slug_integration}`
                            : isCurrentPlan
                              ? `Categoria ativa: ${planFound.name} (Nível ${planFound.level?.name}) - Esta categoria está inserida e funcionando`
                              : `Clique para inserir a categoria ${planFound.name} (Nível ${planFound.level?.name}) no plano ${productERP.name_plan || productERP.plan_slug_integration}`
                        }
                        placement="top"
                      >
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            handleBadgeClick(p, planFound as any);
                          }}
                          isLoading={pendingActionKey === `${p.id}-${planFound?.id || 'null'}`}
                          position="relative"
                          p={1}
                          minW="auto"
                          _hover={{
                            transform: 'scale(1.05)',
                            bg: !isContracted ? 'transparent' : isCurrentPlan ? 'green.100' : 'blue.50',
                          }}
                          transition="all 0.2s"
                          opacity={!isContracted ? 0.5 : 1}
                        >
                          <VStack spacing={0} position="relative">
                            <Box position="relative">
                              <Image
                                src={`/${badge.image}`}
                                alt={badge.name}
                                style={{ height: '20px', width: 'auto' }}
                                opacity={isCurrentPlan ? 1 : !isContracted ? 0.5 : 0.3}
                                filter={isCurrentPlan ? 'none' : !isContracted ? 'grayscale(100%)' : 'grayscale(50%)'}
                              />
                              {!isContracted && !hasSvas && (
                                <Box
                                  position="absolute"
                                  top="50%"
                                  left="0"
                                  right="0"
                                  height="2px"
                                  bg="gray.400"
                                  transform="translateY(-50%) rotate(45deg)"
                                  zIndex={1}
                                />
                              )}
                            </Box>
                          </VStack>
                        </Button>
                      </Tooltip>
                    );
                  }
                })}
            </HStack>
            {p.plans.length === 0 && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  handleBadgeClick(p, null);
                }}
                isLoading={pendingActionKey === `${p.id}-null`}
              >
                <Checkbox
                  size="lg"
                  colorScheme="blue"
                  isChecked={!!productERP.products.find((product) => product.product === p.id)}
                  isReadOnly
                />
              </Button>

            )}
          </Td>
        );
      })}

      {extraData}

      <CreateMapintegration ref={createMapintegrationRef} refetch={refetch} />
    </Tr>
  );
});
IntegrationPlanRow.displayName = 'IntegrationPlanRow';
